import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { DispensaRegistrada, SituacaoDaCompetencia } from '../../../types/chamado'

/**
 * O que este número é: prévia ou fechamento.
 *
 * **É a primeira coisa da tela porque muda a leitura de tudo abaixo.** Um indicador de 93,33% numa
 * competência em preparação quer dizer "é quanto daria se o mês acabasse agora"; o mesmo 93,33%
 * numa competência fechada quer dizer "é quanto vale, e foi isto que foi assinado". Sem o selo, os
 * dois têm exatamente a mesma aparência — e foi assim que o número que sustentou um termo de
 * recebimento passou meses mudando sozinho quando alguém corrigia uma marcação.
 *
 * A dispensa aparece aqui e não junto do redutor porque é ato administrativo, não cálculo: quem lê
 * precisa saber que houve uma decisão, de quem, e com que fundamento — antes de olhar o número que
 * ela zerou.
 *
 * @param props o estado da competência e a dispensa vigente
 * @returns o selo
 */
export function SeloDaCompetencia({
  situacao,
  dispensa,
}: {
  situacao: SituacaoDaCompetencia
  dispensa: DispensaRegistrada | null
}) {
  const fechada = situacao.situacao === 'FECHADA'

  return (
    <div className="space-y-2">
      <div
        className={`rounded-lg px-4 py-3 text-sm ${
          fechada ? 'bg-slate-100 text-slate-800' : 'bg-amber-50 text-amber-900'
        }`}
      >
        <p className="font-semibold">
          {fechada ? TEXTOS.fiscalizacao.selo.fechada : TEXTOS.fiscalizacao.selo.emPreparacao}
        </p>
        <p className="mt-0.5 max-w-prose">
          {fechada ? TEXTOS.fiscalizacao.selo.fechadaAjuda : TEXTOS.fiscalizacao.selo.emPreparacaoAjuda}
        </p>
        {fechada && situacao.fechadaPorNome && (
          <p className="mt-1 text-xs text-slate-600">
            Fechada por {situacao.fechadaPorNome} em {dataHora(situacao.fechadaEm)}
            {situacao.versao > 1 && ` · ${situacao.versao}ª apuração desta competência`}
          </p>
        )}
        {!fechada && situacao.reaberturaMotivo && (
          <p className="mt-1 text-xs text-amber-800">
            {TEXTOS.fiscalizacao.selo.reaberta} {dataHora(situacao.reabertaEm)}: {situacao.reaberturaMotivo}
          </p>
        )}
      </div>

      {dispensa && (
        <div className="rounded-lg bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <p className="font-semibold">{TEXTOS.fiscalizacao.dispensa.selo}</p>
          <p className="mt-0.5 max-w-prose">{dispensa.fundamento}</p>
          <p className="mt-1 text-xs text-sky-800">
            {dispensa.decididaPorNome} · {dataHora(dispensa.decididaEm)}
          </p>
        </div>
      )}
    </div>
  )
}
